import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import NavBar from '../../components/Navbar/NavBar';
import Footer from '../../components/Footer';
import SideMenu from '../../components/SideMenu';
import GalleryComponent from '../../components/Gallery';
import useDocTitle from '../../utils/useDocTitle';
import axios from 'axios';
import ContactUs from '../../components/ContactUs';
import { useLanguage } from '../../context/LanguageContext';

const TalkReadingsDetails = () => {
  const { programId } = useParams();
  const { language } = useLanguage();
  const [talkReading, setTalkReading] = useState(null);
  const [loading, setLoading] = useState(true);

  useDocTitle(
    talkReading ? talkReading.title : language === 'English' ? 'Talk and Reading Details' : 'Detajet e Takimit dhe Leximit'
  );

  useEffect(() => {
    const fetchTalkReadingDetails = async () => {
      try {
        const response = await axios.get(`https://annexpilot.com/dashboard/wp-json/wp/v2/talks_readings/${programId}`);
        const data = response.data;

        if (data.page_language[0].name !== language) {
          setTalkReading(null);
        } else {
          setTalkReading({
            id: data.id,
            title: data.title.rendered,
            subtitle: data.subtitle || '',
            description: data.description || '',
            contentDescription: data.content_description || '',
            talker: {
              name: data.talker_name || 'N/A',
              biography: data.talker_biography || (language === 'English' ? 'Biography not available' : 'Biografia nuk është e disponueshme'),
            },
            coverImage: {
              src: data.poster_image?.guid || '',
              alt: data.poster_image?.post_excerpt || (language === 'English' ? 'Image not available' : 'Imazhi nuk është i disponueshëm'),
            },
            gallery: data.image_gallery?.map((item) => ({
              src: item.guid,
              description: item.post_content,
              alt: item.post_excerpt,
            })) || [],
            videoLinks: data.video_links || [],
          });
        }
      } catch (error) {
        console.error('Error fetching talk reading details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTalkReadingDetails();
  }, [programId, language]);

  if (loading) {
    return <div>{language === 'English' ? 'Loading...' : 'Duke u ngarkuar...'}</div>;
  }

  if (!talkReading) {
    return <div>{language === 'English' ? 'Talk and Reading Details Not Found' : 'Detajet e Takimit dhe Leximit Nuk U Gjetën'}</div>;
  }

  const menuItems = [
    { href: '#overview', label: language === 'English' ? 'Title' : 'Titulli' },
    { href: '#description', label: language === 'English' ? 'Overview' : 'Përmbledhje' },
    { 
      href: '#speaker', 
      label: language === 'English' ? 'Speaker' : 'Folësit',
      subitems: [
        { href: '#speaker-biography', label: language === 'English' ? 'Biography' : 'Biografia' },
      ],
    },
    { href: '#gallery', label: language === 'English' ? 'Gallery' : 'Galeria' },
  ];

  return (
    <>
      <NavBar />

      <div className="flex flex-col md:flex-row min-h-screen">
        <aside className="hidden md:block md:w-1/4 bg-gray-100 px-8 py-12 sticky top-16 h-screen" aria-label="Sidebar navigation for Talk and Reading details">
          <SideMenu
            backLink="/educational-program/talks-reading"
            backText={language === 'English' ? 'Talks and Readings' : 'Takime dhe Lexime'}
            menuItems={menuItems}
          />
        </aside>

        <div className="w-full md:w-3/4 py-6 md:py-12 px-4 md:px-6">
          {/* Overview Section */}
          <div id="overview" className="container-inner mb-8 md:mb-12">
            <img 
              src={talkReading.coverImage.src} 
              alt={talkReading.coverImage.alt} 
              className="w-full h-auto rounded shadow-md mb-6 md:mb-8"
            />
            <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-8" tabIndex="0">
              {talkReading.title}
            </h1>
            {talkReading.subtitle && (
              <h2 className="text-3xl font-semibold text-gray-800" tabIndex="0">
                {talkReading.subtitle}
              </h2>
            )}
          </div>

          {/* Description Section */}
          <div id="description" className="container-inner mb-8 md:mb-12">
            <h2 className="text-2xl font-semibold mb-4" tabIndex="0">{language === 'English' ? 'Overview' : 'Përmbledhje'}</h2>
            <div
              className="text-base md:text-lg text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: talkReading.contentDescription }}
              tabIndex="0"
              aria-label={language === 'English' ? 'Content description' : 'Përshkrimi i përmbajtjes'}
            ></div>
          </div>

          {/* Speaker Section */}
          {talkReading.talker.name && (
            <div id="speaker" className="container-inner mb-8 md:mb-12">
              <h2 className="text-2xl font-semibold mb-4" tabIndex="0">{language === 'English' ? 'Speaker' : 'Folësit'}</h2>
              <div id="speaker-biography">
                <h3 className="text-xl font-semibold mb-4" tabIndex="0">{talkReading.talker.name}</h3>
                <div
                  className="text-base md:text-lg text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: talkReading.talker.biography }}
                  tabIndex="0"
                  aria-label={language === 'English' ? `Biography of ${talkReading.talker.name}` : `Biografia e ${talkReading.talker.name}`}
                ></div>
              </div>
            </div>
          )}

          {/* Gallery Section */}
          {talkReading.gallery.length > 0 && (
            <div id="gallery" className="container-inner mb-8 md:mb-12">
              <h2 className="text-2xl font-semibold mb-4" tabIndex="0">{language === 'English' ? 'Gallery' : 'Galeria'}</h2>
              <GalleryComponent images={talkReading.gallery} />
            </div>
          )}

          <ContactUs />
        </div>
      </div>

      <Footer />
    </>
  );
};

export default TalkReadingsDetails;